export const TIP_PERCENTS = [18, 20, 25, 30] as const
export type TipPercent = (typeof TIP_PERCENTS)[number]

export const DEFAULT_TIP_PERCENT: TipPercent = 20

/** Below this, the roast treats you as cheap. 18% is the floor, not a suggestion. */
export const CHEAP_TIP_BELOW = 18

/** At or above this, we ask if the bartender was cute. */
export const GENEROUS_TIP_AT = 25

export const VERY_GENEROUS_TIP_AT = 30

export function dollarsToCents(value: number): number {
  if (!Number.isFinite(value)) return 0
  return Math.round(value * 100)
}

export function centsToDollars(cents: number): number {
  return cents / 100
}

export function roundCents(cents: number): number {
  return Math.round(cents)
}

export function tipCentsFromBill(billCents: number, percent: number): number {
  return roundCents((billCents * percent) / 100)
}

export function grandTotalCents(billCents: number, tipCents: number): number {
  return billCents + tipCents
}

/** Out-the-door total when auto-grat is a separate line on the check. */
export function tabPayTotalCents(
  billCents: number,
  tipCents: number,
  autoGratCents = 0,
): number {
  return billCents + Math.max(0, autoGratCents) + tipCents
}

export type AutoGratEntryMode = 'percent' | 'amount'

/** Resolve auto-grat cents from bill subtotal and either a % or fixed dollars. */
export function resolveAutoGratCents(
  billCents: number,
  enabled: boolean,
  mode: AutoGratEntryMode,
  autoGratDollars: number | null,
  autoGratPercent: number | null,
): number {
  if (!enabled || billCents <= 0) return 0
  if (mode === 'percent' && autoGratPercent != null) {
    return tipCentsFromBill(billCents, autoGratPercent)
  }
  if (mode === 'amount' && autoGratDollars != null) {
    return dollarsToCents(autoGratDollars)
  }
  return 0
}

export function tipPercentFromAmounts(billCents: number, tipCents: number): number {
  if (billCents <= 0) return 0
  return Math.round((tipCents / billCents) * 1000) / 10
}

export function formatMoney(cents: number): string {
  return centsToDollars(cents).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  })
}

export function formatPercent(percent: number): string {
  const rounded = Math.round(percent * 10) / 10
  return Number.isInteger(rounded) ? `${rounded}%` : `${rounded.toFixed(1)}%`
}

/** Parse a typed tip percent (allows decimals). Empty or junk → null. */
export function parsePercentInput(raw: string): number | null {
  const cleaned = raw.replace(/[^0-9.]/g, '')
  if (!cleaned) return null
  const value = Number.parseFloat(cleaned)
  if (!Number.isFinite(value) || value < 0 || value > 500) return null
  return value
}

/** Parse a typed dollar amount. Empty or junk becomes null so the field can stay blank. */
export function parseDollarInput(raw: string): number | null {
  const cleaned = raw.replace(/[^0-9.]/g, '')
  if (!cleaned) return null
  const value = Number.parseFloat(cleaned)
  if (!Number.isFinite(value) || value < 0) return null
  return value
}

export function todayISODate(now = new Date()): string {
  const y = now.getFullYear()
  const m = String(now.getMonth() + 1).padStart(2, '0')
  const d = String(now.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

export function startOfWeek(date: Date): Date {
  const copy = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  copy.setDate(copy.getDate() - copy.getDay())
  copy.setHours(0, 0, 0, 0)
  return copy
}

export function addDays(date: Date, days: number): Date {
  const copy = new Date(date)
  copy.setDate(copy.getDate() + days)
  return copy
}

export function parseISODate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y, (m ?? 1) - 1, d ?? 1)
}

export function formatShortDate(iso: string): string {
  return parseISODate(iso).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  })
}

export function formatWeekLabel(weekStart: Date): string {
  const weekEnd = addDays(weekStart, 6)
  const sameMonth = weekStart.getMonth() === weekEnd.getMonth()
  const start = weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  const end = weekEnd.toLocaleDateString('en-US', {
    month: sameMonth ? undefined : 'short',
    day: 'numeric',
  })
  return `${start} – ${end}`
}

export function isThisWeek(iso: string, now = new Date()): boolean {
  const weekStart = startOfWeek(now)
  const weekEnd = addDays(weekStart, 7)
  const date = parseISODate(iso)
  return date >= weekStart && date < weekEnd
}

export function weeksAgo(iso: string, now = new Date()): number {
  const weekStart = startOfWeek(now).getTime()
  const tabWeek = startOfWeek(parseISODate(iso)).getTime()
  return Math.round((weekStart - tabWeek) / (7 * 24 * 60 * 60 * 1000))
}
